import React from 'react';
import { GlobalOutlined, CheckOutlined } from '@ant-design/icons';
import styles from './LanguageSelector.module.css';
import { useLanguage, type Language } from '@/i18n';
import { FmButton, FmMenu, type FmMenuItem } from '@/components/ui';

interface LanguageSelectorProps {
  className?: string;
  style?: React.CSSProperties;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ className = '', style = {} }) => {
  const { language, setLanguage } = useLanguage();

  const languages: Array<{ code: Language; name: string; flag: string }> = [
    { code: 'fi', name: 'Suomi', flag: '🇫🇮' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
  ];

  const items: FmMenuItem[] = languages.map((lang) => {
    const isActive = language === lang.code;

    return {
      key: lang.code,
      title: lang.name,
      label: (
        <span
          aria-current={isActive ? 'true' : undefined}
          data-testid={`language-option-${lang.code}`}
          className={styles.languageSelectorOption}
        >
          <span className={styles.languageSelectorFlag}>{lang.flag}</span>
          <span className={styles.languageSelectorName}>{lang.name}</span>
          <span className={styles.languageSelectorCode}>{lang.code.toUpperCase()}</span>
          {isActive && (
            <CheckOutlined
              data-testid={`language-option-${lang.code}-active-indicator`}
              className={styles.languageSelectorCheckIcon}
            />
          )}
        </span>
      ),
      onClick: () => setLanguage(lang.code),
    };
  });

  const activeLanguage = languages.find((l) => l.code === language);

  return (
    <div
      className={`${styles.languageSelector} ${className}`}
      style={style}
      aria-label={activeLanguage?.name}
    >
      <FmMenu
        items={items}
        trigger="hover"
        placement="bottomRight"
        menuTestId="language-selector-menu"
      >
        <FmButton
          data-testid="language-selector-trigger"
          variant="text"
          size="sm"
          leadingIcon={<GlobalOutlined />}
          title={activeLanguage?.name}
          className={styles.languageSelectorButton}
        >
          {activeLanguage?.code.toUpperCase()}
        </FmButton>
      </FmMenu>
    </div>
  );
};

export default LanguageSelector;
